import React from "react"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"
import type { FormDescriptionImage } from "@f2e/shared/types"
import { FieldDescription } from "@f2e/ui/components/InteractionRequestForm"

import {
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@f2e/ui/components/base"

import { BaseFieldEditor, BaseFieldProps } from "./BaseFieldEditor"
import { OptionsEditor } from "./OptionsEditor"

export type DropdownField = BaseFieldProps<{
  type: "dropdown"
  name: string
  question: string
  label: string
  placeholder: string
  required: boolean
  options?: { label: string; value: string }[]
}>

export const DropdownFieldEditor: React.FC<DropdownField> = ({
  field,
  onChange,
  disabled,
}) => {
  return (
    <>
      <BaseFieldEditor
        field={field}
        onChange={onChange}
        disabled={disabled}
        showPlaceholder={false}
      />
      <OptionsEditor field={field} onChange={onChange} disabled={disabled} />
    </>
  )
}

export default DropdownFieldEditor

// Unified renderer that supports both preview and runtime modes, reusing the same JSX
export const DropdownFieldRenderer: React.FC<{
  field: {
    question: string
    options?: { label: string; value: string }[]
    required: boolean
    placeholder: string
    helpText?: string
    descriptionImage?: FormDescriptionImage
  }
  mode: "preview" | "runtime"
  value?: string
  onChange?: (v: string) => void
  disabled?: boolean
  error?: string
}> = ({ field, mode, value, onChange, disabled, error }) => {
  const { t } = useTranslation()
  const opts = field.options || []
  const [local, setLocal] = React.useState<string | undefined>(undefined)
  const isPreview = mode === "preview"
  const val = isPreview ? local : value
  const setVal = (v: string) => {
    if (isPreview) setLocal(v)
    else onChange?.(v)
  }
  return (
    <div className="space-y-1">
      <Label className="text-xs">
        {field.question}{" "}
        {field.required ? <span className="text-red-500">*</span> : null}
      </Label>
      <FieldDescription
        helpText={field.helpText}
        descriptionImage={field.descriptionImage}
      />
      <Select value={val} onValueChange={setVal} disabled={disabled}>
        <SelectTrigger
          className={error ? "border-red-500 focus:ring-red-500" : undefined}
        >
          <SelectValue
            placeholder={
              field.placeholder ||
              t(
                "workspace.agent.basicForm.formBuilder.placeholders.selectOption"
              )
            }
          />
        </SelectTrigger>
        <SelectContent>
          {opts.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error ? <div className="text-xs text-red-500">{error}</div> : null}
    </div>
  )
}

export function serializeDropdownField(
  field: { options?: { label: string; value: string }[] },
  raw: unknown
): unknown {
  const options = Array.isArray(field.options) ? field.options : []
  const found = options.find((o) => o.value === raw)
  return found ? found.label : raw
}

export function validateDropdownField(
  field: { name: string; required?: boolean },
  value: unknown,
  t: TFunction
): string | undefined {
  if (!field.required) return undefined
  const str = value === undefined || value === null ? "" : String(value)
  if (str.trim() === "")
    return t("workspace.agent.basicForm.formBuilder.validation.required")
  return undefined
}
